import type { FormEvent } from 'react';
import React, { useState } from 'react';
import { range } from 'lodash-es';
import styled from 'styled-components';

import {
  AdornedInput,
  Adornment,
  FormField,
  IconPlusOutline,
  Input,
  Label,
  Paragraph,
  Spacer,
  Text,
  TextButton,
} from '@zillow/constellation';
import { Center, Col, CurrencyInput, Row } from '@zillow/zrs-comet';

import { CenteredHR } from '../../Widgets/CenteredHR';
import YesAndNo from '../../Widgets/YesAndNo';
import { Field, NonRefundableFeesSection } from '../../../models/index';
import { NON_REFUNDABLE_FEES } from '../../../constants/index';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../constants/stringConstants';
import { isYesNoEnabledForNumberedFields } from '../../../constants/functionConstants';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_FEES = 5;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  height: 1rem;
`;

type Props = {
  section: NonRefundableFeesSection;
  setSectionValues: (object) => void;
  setSection: (section: NonRefundableFeesSection) => void;
};

export default function NonRefundableFees_v1({ section, setSectionValues, setSection }: Props): JSX.Element {
  const [hasClickedNo, setHasClickedNo] = useState(false);

  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: NonRefundableFeesSection,
  });

  function onRemoveAll(): void {
    setHasClickedNo(true);
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function setAmount(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.AMOUNT, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setName(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function renderFee(index: number): React.ReactNode {
    const nameField = section.getField(Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.NAME, index));
    const amountField = section.getField(Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.AMOUNT, index));

    return (
      <Spacer marginBottom="sm">
        {index > 0 && (
          <Row paddingY="xs">
            <Col xs={8}>
              <HeaderContainer>
                <Text fontType="bodyHeading">Non-refundable fee #{index + 1}</Text>
              </HeaderContainer>
            </Col>
            <Col xs={4} align="flex-end">
              <HeaderContainer data-index={index} data-test="delete-fee" onClick={onDeleteOne}>
                <TextButton>Delete</TextButton>
              </HeaderContainer>
            </Col>
          </Row>
        )}
        <FormField marginBottom="xs">
          <Label id={`fee-name-${index}`}>{NON_REFUNDABLE_FEES.LABEL.NAME}</Label>
          <Input
            aria-labelledby={`fee-name-${index}`}
            data-index={index}
            data-test="name"
            value={nameField.enteredValue}
            placeholder="(example: Move-in fee)"
            onChange={setName}
          />
        </FormField>
        <FormField>
          <Label id={`amount-${index}`}>{NON_REFUNDABLE_FEES.LABEL.AMOUNT}</Label>
          <AdornedInput
            inputId={`AdornedInput-amount-${index}`}
            leftAdornment={<Adornment>$</Adornment>}
            input={
              <CurrencyInput
                aria-labelledby={`amount-${index}`}
                data-index={index}
                data-test="amount"
                value={amountField.enteredValue}
                placeholder={CURRENCY_INPUT_PLACEHOLDER}
                onChange={setAmount}
                onBlur={setAmount}
              />
            }
          />
        </FormField>
      </Spacer>
    );
  }

  const totalNonRefundableFees = section.getTotalIndexes();
  const isEnabled = isYesNoEnabledForNumberedFields(section, 'nonRefundableFees0_isEnabled', hasClickedNo);

  if (totalNonRefundableFees == null) {
    return null;
  }

  return (
    <>
      <Text fontType="h6">Will there be non-refundable fees?</Text>
      <Paragraph marginBottom="sm">
        Examples include administration and move-in fees that are collected at signing.
      </Paragraph>
      <Spacer marginBottom={totalNonRefundableFees > 0 ? 'sm' : ''}>
        <YesAndNo yesHandler={onAddOne} noHandler={onRemoveAll} yesActive={isEnabled} />
      </Spacer>
      {totalNonRefundableFees > 0 && (
        <Paragraph marginBottom="xs">
          The total of these fees should be less than one month’s rent. Do not include pet fees, which are listed
          elsewhere.
        </Paragraph>
      )}
      {range(0, totalNonRefundableFees).map((index: number) => (
        <React.Fragment key={index}>
          {index > 0 && <CenteredHR />}
          {renderFee(index)}
        </React.Fragment>
      ))}
      <Center>
        {totalNonRefundableFees < MAXIMUM_FEES ? (
          totalNonRefundableFees > 0 && (
            <TextButton data-test="add-another" icon={<IconPlusOutline />} onClick={onAddOne}>
              Add another non-refundable fee
            </TextButton>
          )
        ) : (
          <Text>
            <Center>
              <span data-test="maximum-reached">
                You’ve added {MAXIMUM_FEES} non-refundable fees, which is the maximum allowed.
              </span>
            </Center>
          </Text>
        )}
      </Center>
    </>
  );
}
